/**
 * ErrorMessageCard - Distinct error display for failed assistant responses
 *
 * Renders error/fallback messages differently from normal assistant messages:
 * - Red/error styling instead of neutral bubble
 * - Error icon for visual distinction
 * - Retry button for actionable recovery
 * - Context-specific guidance
 */

import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, RotateCw, Flag, MessageSquarePlus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'

interface ErrorMessageCardProps {
  /** Error message to display */
  message: string
  /** What the reader can do to resolve the failure, in the message's language
   *  (a turn_failure record's options) */
  options?: readonly string[]
  /** Opens a new conversation with the agent whose turn failed */
  onNewConversation?: () => void
  /** Retry callback */
  onRetry?: () => void
  /** Whether retry is in progress */
  isRetrying?: boolean
  /**
   * Report callback — when provided, a "Report" action flags this failed
   * answer through the message-feedback flow so it lands in the team's triage
   * queue (the worst-case action when a retry won't help). May return a promise
   * — "Reported" only shows once it resolves, so a failed report isn't falsely
   * confirmed.
   */
  onReport?: () => void | Promise<unknown>
  /** Additional CSS classes */
  className?: string
}

/** The filled action that recovers from the failure (retry, new conversation). */
const recoveryButtonClass = cn(
  'flex items-center gap-2 px-4 py-2',
  'text-sm font-medium',
  'bg-error-100 text-error-700',
  'hover:bg-error-200 active:bg-error-300',
  'border border-error-300 rounded-lg',
  'transition-colors duration-150',
  'min-h-[44px]' // iOS touch target
)

export const ErrorMessageCard = memo(function ErrorMessageCard({
  message,
  options,
  onNewConversation,
  onRetry,
  isRetrying = false,
  onReport,
  className,
}: ErrorMessageCardProps) {
  const { t } = useTranslation()
  const [reported, setReported] = useState(false)

  return (
    <Card
      role="alert"
      data-testid="error-message-card"
      className={cn(
        'flex gap-3 p-4 border-error-300 bg-error-50',
        'animate-message-appear',
        className
      )}
    >
      {/* Error icon */}
      <AlertCircle
        className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5"
        aria-hidden="true"
      />

      {/* Error content */}
      <div className="flex-1 min-w-0 space-y-3">
        <p className="text-sm text-error-900 leading-relaxed whitespace-pre-wrap">
          {message}
        </p>

        {options && options.length > 0 && (
          <ul className="list-disc space-y-1 pl-5 text-sm text-error-900 leading-relaxed">
            {options.map((option) => (
              <li key={option}>{option}</li>
            ))}
          </ul>
        )}

        {/* Actions: Retry recovers in place; New conversation leaves a session
            whose agent version can no longer run; Report is the worst-case
            action (flags the failed answer into the team's triage queue). */}
        {(onRetry || onNewConversation || onReport) && (
          <div className="flex flex-wrap items-center gap-2">
            {onNewConversation && (
              <button onClick={onNewConversation} className={recoveryButtonClass}>
                <MessageSquarePlus className="w-4 h-4" />
                <span>{t('session.newConversation')}</span>
              </button>
            )}
            {onRetry && (
              <button
                onClick={onRetry}
                disabled={isRetrying}
                className={cn(recoveryButtonClass, 'disabled:opacity-50 disabled:cursor-not-allowed')}
                aria-label={isRetrying ? t('chat.errors.retrying') : t('chat.errors.retryButton')}
              >
                <RotateCw
                  className={cn(
                    'w-4 h-4',
                    isRetrying && 'animate-spin'
                  )}
                />
                <span>{isRetrying ? t('chat.errors.retrying') : t('chat.errors.retryButton')}</span>
              </button>
            )}
            {onReport && (
              <button
                onClick={() => {
                  if (reported) return
                  // Show "Reported" only after the report resolves — on failure
                  // leave the button so the nurse can retry (the feedback hook's
                  // own onError toast surfaces the failure).
                  void Promise.resolve(onReport())
                    .then(() => setReported(true))
                    .catch(() => {})
                }}
                disabled={reported}
                className={cn(
                  'flex items-center gap-2 px-4 py-2',
                  'text-sm font-medium',
                  'text-error-700 hover:bg-error-100 active:bg-error-200',
                  'border border-error-300 rounded-lg',
                  'transition-colors duration-150',
                  'disabled:opacity-60 disabled:cursor-default',
                  'min-h-[44px]' // iOS touch target
                )}
                aria-label={reported ? t('actions.reported', 'Reported') : t('actions.report', 'Report')}
              >
                <Flag className="w-4 h-4" />
                <span>{reported ? t('actions.reported', 'Reported') : t('actions.report', 'Report')}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  )
})
